/**
 * Снимок таблицы лидеров для экрана (ТЗ FR-LB-01, FR-LB-06, FR-LB-07).
 *
 * Запрос делается при открытии экрана и по явному действию человека —
 * никогда во время набора: сеть в пути нажатия запрещена (ТЗ NFR-P-03).
 *
 * Молчание сервера и пустая таблица — разные вещи. Гостю рейтинг не отвечает
 * вовсе: место в нём считается по засчитанным контрольным результатам, а они
 * привязаны к аккаунту. Показать гостю пустую таблицу значило бы сказать «вы
 * последний», хотя его там просто нет.
 */

import { useCallback, useEffect, useState } from 'react';
import type { LeaderboardBoard, LeaderboardKind } from '@type2x/feature-leaderboard';

import type { Runtime } from '../runtime.js';

/** Что известно о таблице прямо сейчас. */
export type LeaderboardState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly board: LeaderboardBoard }
  /** Сервер не ответил: гость, нет связи или закрытая сессия. */
  | { readonly status: 'unavailable' };

export interface LeaderboardAccess {
  readonly state: LeaderboardState;
  /** Какой вид таблицы показан: у каждого своё измерение (ТЗ FR-LB-01). */
  readonly kind: LeaderboardKind;
  /** Сменить вид: таблица перечитывается с сервера, а не сортируется здесь. */
  readonly selectKind: (kind: LeaderboardKind) => void;
  /** Перечитать таблицу: после смены участия или псевдонима. */
  readonly refresh: () => void;
  /** Сохранить участие и псевдоним; `false` — сервер не принял. */
  readonly save: (options: {
    readonly optIn?: boolean;
    readonly displayName?: string;
  }) => Promise<boolean>;
}

export function useLeaderboard(
  runtime: Runtime,
  language: string,
  initialKind: LeaderboardKind = 'progress',
): LeaderboardAccess {
  const [state, setState] = useState<LeaderboardState>({ status: 'loading' });
  const [kind, setKind] = useState<LeaderboardKind>(initialKind);
  const [asked, setAsked] = useState(0);

  useEffect(() => {
    let current = true;

    void runtime.leaderboard
      .board(kind, language)
      .then((board) => {
        if (!current) return;
        setState(board === null ? { status: 'unavailable' } : { status: 'ready', board });
      })
      .catch(() => {
        if (current) setState({ status: 'unavailable' });
      });

    return () => {
      current = false;
    };
  }, [runtime, kind, language, asked]);

  const refresh = useCallback(() => setAsked((count) => count + 1), []);
  /**
   * Смена вида — новый запрос, а не пересортировка полученного: порядок
   * считает сервер, и другого порядка у клиента взяться неоткуда.
   *
   * Прежняя таблица при этом убирается: показывать порядок Progress под
   * заголовком Accuracy — ровно та ошибка, из-за которой этот выбор и
   * появился.
   */
  const selectKind = useCallback(
    (next: LeaderboardKind) => {
      if (next === kind) return;
      setState({ status: 'loading' });
      setKind(next);
    },
    [kind],
  );

  const save = useCallback(
    async (options: { optIn?: boolean; displayName?: string }): Promise<boolean> => {
      try {
        await runtime.leaderboard.updateParticipation({ language, ...options });
        setAsked((count) => count + 1);
        return true;
      } catch {
        // Отказ не прячется: человек нажал «участвовать», и если сервер не
        // принял, он должен это увидеть, а не думать, что участвует.
        return false;
      }
    },
    [runtime, language],
  );

  return { state, kind, selectKind, refresh, save };
}
